/**
 * Recipe API Utilities
 * Fetches recipe data from WordPress REST API with JSON file fallback
 */
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
/**
 * Get WordPress REST API base URL
 */
function getApiUrl() {
    const wpData = window.wpData;
    if (wpData === null || wpData === void 0 ? void 0 : wpData.apiUrl) {
        // Convert wp/v2/ to noads/v1/
        return wpData.apiUrl.replace('wp/v2/', 'noads/v1/');
    }
    // Fallback to default WordPress REST API URL
    return '/wp-json/noads/v1/';
}
/**
 * Fetch recipe index from WordPress REST API
 */
export function fetchRecipeIndex() {
    return __awaiter(this, arguments, void 0, function* (params = {}) {
        try {
            const apiUrl = getApiUrl();
            const url = `${apiUrl}recipes`;
            // Build query string
            const queryParams = new URLSearchParams();
            if (params.per_page !== undefined)
                queryParams.set('per_page', params.per_page.toString());
            if (params.page !== undefined)
                queryParams.set('page', params.page.toString());
            if (params.orderby)
                queryParams.set('orderby', params.orderby);
            if (params.order)
                queryParams.set('order', params.order);
            if (params.category)
                queryParams.set('category', params.category);
            if (params.flame !== undefined)
                queryParams.set('flame', params.flame.toString());
            if (params.search)
                queryParams.set('search', params.search);
            const fullUrl = url + (queryParams.toString() ? '?' + queryParams.toString() : '');
            const response = yield fetch(fullUrl, {
                method: 'GET',
                credentials: 'include',
                headers: {
                    'Content-Type': 'application/json',
                },
            });
            if (!response.ok) {
                throw new Error(`API request failed: ${response.status} ${response.statusText}`);
            }
            const data = yield response.json();
            // Filter items based on expose_in_recipes_index if present
            if (data.items && Array.isArray(data.items)) {
                data.items = data.items.filter((item) => {
                    // Include if expose_in_recipes_index is true or not set
                    return item.expose_in_recipes_index !== false;
                });
            }
            return data;
        }
        catch (error) {
            console.error('[RecipeAPI] Error fetching recipe index:', error);
            throw error;
        }
    });
}
/**
 * Fetch recipe from WordPress REST API
 */
export function fetchRecipeFromApi(slug) {
    return __awaiter(this, void 0, void 0, function* () {
        try {
            const apiUrl = getApiUrl();
            const url = `${apiUrl}recipes/${encodeURIComponent(slug)}`;
            console.log('[RecipeAPI] Fetching recipe from API:', url);
            const response = yield fetch(url, {
                method: 'GET',
                credentials: 'include',
                headers: {
                    'Content-Type': 'application/json',
                },
            });
            if (!response.ok) {
                if (response.status === 404) {
                    console.warn('[RecipeAPI] Recipe not found in API (404):', slug);
                    return null; // Recipe not found
                }
                const errorText = yield response.text().catch(() => 'Unknown error');
                console.error('[RecipeAPI] API request failed:', {
                    status: response.status,
                    statusText: response.statusText,
                    slug,
                    error: errorText
                });
                throw new Error(`API request failed: ${response.status} ${response.statusText}`);
            }
            const data = yield response.json();
            console.log('[RecipeAPI] Successfully loaded recipe from API:', slug);
            return data;
        }
        catch (error) {
            console.error('[RecipeAPI] Error fetching from WP REST API:', error);
            throw error;
        }
    });
}
/**
 * Search for recipe JSON file in repo subfolders
 * Tries: mains, sides, assemblies, drinks
 */
export function fetchRecipeFromJson(slug) {
    return __awaiter(this, void 0, void 0, function* () {
        const repoBase = '/wp-content/noads/repo/';
        const subfolders = ['mains', 'sides', 'assemblies', 'drinks'];
        for (const folder of subfolders) {
            try {
                const url = `${repoBase}${folder}/${encodeURIComponent(slug)}.json`;
                const response = yield fetch(url, {
                    method: 'GET',
                    cache: 'no-store',
                });
                if (response.ok) {
                    const data = yield response.json();
                    return data;
                }
            }
            catch (error) {
                // Continue to next folder
                continue;
            }
        }
        return null; // Not found in any folder
    });
}
/**
 * Load recipe with fallback: WP REST API -> JSON files
 */
export function loadRecipe(slug) {
    return __awaiter(this, void 0, void 0, function* () {
        if (!slug) {
            throw new Error('Recipe slug is required');
        }
        console.log('[RecipeAPI] loadRecipe called with slug:', slug);
        // Try WP REST API first
        try {
            const apiRecipe = yield fetchRecipeFromApi(slug);
            if (apiRecipe) {
                console.log('[RecipeAPI] ✓ Loaded recipe from WP REST API:', slug);
                return apiRecipe;
            }
            console.log('[RecipeAPI] Recipe not found in API, trying JSON fallback...');
        }
        catch (error) {
            console.warn('[RecipeAPI] WP REST API failed, trying JSON fallback:', error);
        }
        // Fallback to JSON files
        try {
            const jsonRecipe = yield fetchRecipeFromJson(slug);
            if (jsonRecipe) {
                console.log('[RecipeAPI] ✓ Loaded recipe from JSON fallback:', slug);
                return jsonRecipe;
            }
            console.log('[RecipeAPI] Recipe not found in JSON files either');
        }
        catch (error) {
            console.error('[RecipeAPI] JSON fallback also failed:', error);
        }
        console.error('[RecipeAPI] ✗ Recipe not found in any source:', slug);
        return null; // Not found in either source
    });
}
